import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Grid,
  Chip
} from '@mui/material';
import { toast } from 'react-toastify';
import { MessageCircle, FileText, Type } from 'lucide-react';
import api from '../../services/api';

const parseJsonSafe = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

export default function SpokiSendMessageModal({
  open,
  onClose,
  defaultPhone,
  defaultCustomerId,
  defaultPracticeId,
  defaultContactId,
  defaultEntityType,
  defaultEntityId
}) {
  const [loading, setLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [defaultChannel, setDefaultChannel] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [mode, setMode] = useState('template');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [language, setLanguage] = useState('it');
  const [phone, setPhone] = useState(defaultPhone || '');
  const [text, setText] = useState('');
  const [customFields, setCustomFields] = useState('{}');
  const [buttons, setButtons] = useState('[]');
  const [header, setHeader] = useState('{}');

  useEffect(() => {
    if (open) {
      setPhone(defaultPhone || '');
      loadSettings();
      loadTemplates();
    }
  }, [open]);

  const loadSettings = async () => {
    try {
      setSettingsLoading(true);
      const params = {};
      if (defaultEntityType && defaultEntityId) {
        params.entity_type = defaultEntityType;
        params.entity_id = defaultEntityId;
      }
      const res = await api.get('/spoki/settings', { params });
      const data = res.data || {};
      const acc = data.accounts || [];
      const ch = data.channels || [];
      setAccounts(acc);
      setChannels(ch);
      setDefaultChannel(data.defaultChannel || null);
      if (acc.length > 0) {
        setSelectedAccountId(String(acc[0].id));
      }
      if (data.defaultChannel) {
        setSelectedChannelId(String(data.defaultChannel.spoki_channel_id));
      } else if (ch.length > 0) {
        setSelectedChannelId(String(ch[0].id));
      }
    } catch (error) {
      toast.error('Errore nel caricamento delle impostazioni Spoki');
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const res = await api.get('/spoki/templates');
      const items = res.data || [];
      setTemplates(items);
      if (items.length > 0) {
        setSelectedTemplateId(String(items[0].id));
        const first = items[0];
        if (first.default_language) {
          setLanguage(first.default_language);
        }
      }
    } catch (error) {
      toast.error('Errore nel caricamento dei template Spoki');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    onClose && onClose();
  };

  const handleSend = async () => {
    try {
      if (!phone) {
        toast.error('Inserire il numero di telefono');
        return;
      }
      if (mode === 'template' && !selectedTemplateId) {
        toast.error('Selezionare un template');
        return;
      }
      if (mode === 'text' && !text) {
        toast.error('Inserire il testo del messaggio');
        return;
      }
      setLoading(true);
      const payloadBase = {
        spoki_account_id: selectedAccountId ? Number(selectedAccountId) : null,
        spoki_channel_id: selectedChannelId ? Number(selectedChannelId) : null,
        entity_type: defaultEntityType || null,
        entity_id: defaultEntityId || null,
        customer_id: defaultCustomerId || null,
        practice_id: defaultPracticeId || null,
        contact_id: defaultContactId || null,
        phone
      };
      if (mode === 'template') {
        const payload = {
          ...payloadBase,
          template_id: Number(selectedTemplateId),
          language,
          fields: parseJsonSafe(customFields),
          buttons: parseJsonSafe(buttons),
          header: parseJsonSafe(header)
        };
        const res = await api.post('/spoki/messages/send-template', payload);
        if (res.data && res.data.success) {
          toast.success('Template Spoki inviato correttamente');
          handleClose();
        } else {
          toast.error('Errore durante l’invio del template Spoki');
        }
      } else {
        const payload = {
          ...payloadBase,
          text,
          metadata: parseJsonSafe(customFields)
        };
        const res = await api.post('/spoki/messages/send-text', payload);
        if (res.data && res.data.success) {
          toast.success('Messaggio Spoki inviato correttamente');
          handleClose();
        } else {
          toast.error('Errore durante l’invio del messaggio Spoki');
        }
      }
    } catch (error) {
      toast.error('Errore nella richiesta verso il backend Spoki');
    } finally {
      setLoading(false);
    }
  };

  const selectedTemplate =
    templates.find((t) => String(t.id) === String(selectedTemplateId)) || null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Invio WhatsApp Spoki
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            exclusive
            value={mode}
            onChange={(_, value) => {
              if (value) setMode(value);
            }}
            size="small"
          >
            <ToggleButton value="template">
              <FileText size={16} style={{ marginRight: 8 }} />
              Template
            </ToggleButton>
            <ToggleButton value="text">
              <Type size={16} style={{ marginRight: 8 }} />
              Testo libero
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Telefono"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              fullWidth
              margin="dense"
            />
            <FormControl fullWidth margin="dense" disabled={settingsLoading}>
              <InputLabel id="spoki-account-label">Account Spoki</InputLabel>
              <Select
                labelId="spoki-account-label"
                value={selectedAccountId}
                label="Account Spoki"
                onChange={(e) => setSelectedAccountId(e.target.value)}
              >
                {accounts.map((a) => (
                  <MenuItem key={a.id} value={String(a.id)}>
                    {a.account_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense" disabled={settingsLoading}>
              <InputLabel id="spoki-channel-label">Numero/Canale</InputLabel>
              <Select
                labelId="spoki-channel-label"
                value={selectedChannelId}
                label="Numero/Canale"
                onChange={(e) => setSelectedChannelId(e.target.value)}
              >
                {channels
                  .filter((c) =>
                    selectedAccountId ? String(c.spoki_account_id) === String(selectedAccountId) : true
                  )
                  .map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.display_name || c.phone}{' '}
                      {defaultChannel &&
                        Number(defaultChannel.spoki_channel_id) === Number(c.id) && (
                          <Chip
                            size="small"
                            color="primary"
                            label="Predefinito"
                            sx={{ ml: 1 }}
                          />
                        )}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            {mode === 'template' && (
              <>
                <FormControl fullWidth margin="dense" disabled={templatesLoading}>
                  <InputLabel id="spoki-template-label">Template</InputLabel>
                  <Select
                    labelId="spoki-template-label"
                    value={selectedTemplateId}
                    label="Template"
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                  >
                    {templates.map((t) => (
                      <MenuItem key={t.id} value={String(t.id)}>
                        {t.name} {t.status && `(${t.status})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Lingua"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  fullWidth
                  margin="dense"
                />
              </>
            )}
            {mode === 'text' && (
              <TextField
                label="Testo messaggio"
                value={text}
                onChange={(e) => setText(e.target.value)}
                fullWidth
                margin="dense"
                multiline
                minRows={4}
              />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {mode === 'template' && (
              <>
                <TextField
                  label="Custom fields (JSON)"
                  value={customFields}
                  onChange={(e) => setCustomFields(e.target.value)}
                  fullWidth
                  margin="dense"
                  multiline
                  minRows={3}
                />
                <TextField
                  label="Buttons (JSON array)"
                  value={buttons}
                  onChange={(e) => setButtons(e.target.value)}
                  fullWidth
                  margin="dense"
                  multiline
                  minRows={3}
                />
                <TextField
                  label="Header (JSON)"
                  value={header}
                  onChange={(e) => setHeader(e.target.value)}
                  fullWidth
                  margin="dense"
                  multiline
                  minRows={2}
                />
              </>
            )}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Anteprima payload
              </Typography>
              <Box
                sx={{
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  p: 1.5,
                  fontFamily: 'monospace',
                  fontSize: 12,
                  maxHeight: 220,
                  overflow: 'auto'
                }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(
                    mode === 'template'
                      ? {
                          phone,
                          template_id: selectedTemplateId,
                          language,
                          fields: parseJsonSafe(customFields),
                          buttons: parseJsonSafe(buttons),
                          header: parseJsonSafe(header)
                        }
                      : {
                          phone,
                          text,
                          metadata: parseJsonSafe(customFields)
                        },
                    null,
                    2
                  )}
                </pre>
              </Box>
              {selectedTemplate && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MessageCircle size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Template selezionato: {selectedTemplate.name}{' '}
                    {selectedTemplate.status && `(${selectedTemplate.status})`}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annulla
        </Button>
        <Button
          onClick={handleSend}
          disabled={loading}
          variant="contained"
          color="primary"
          startIcon={<MessageCircle size={18} />}
        >
          Invia
        </Button>
      </DialogActions>
    </Dialog>
  );
}

